/**
 * Company Edit → Activity — Communication History (`pmc_type=COMPANY`).
 * Created Date: same `AdvancedDateRangePicker` pattern as Invitee list filters:
 * Angular communication-log presets, presets on top, two stacked months, APPLY defers preset until confirm → `fromDt`/`toDt` (DD-MM-YYYY).
 *
 * Table columns use API fields `log_datetime` and `created_at` (same as Angular `communication-log-list`);
 * they may differ — log time is the event, created time is when the row was saved.
 */
import { useState, useEffect, useMemo, type FormEvent } from "react";
import { History, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils/helpers";
import { COMM_FIELD_BASE, COMM_LABEL } from "@/lib/styles/companyEditTabShell";
import apiClient from "@/lib/api/apiClient";
import { getBrowserApiBaseUrl } from "@/config/env";
import { getAuthTokenFromStorage } from "@/lib/utils/storage";
import { showError, showSuccess, formatApiError } from "@/lib/utils/notifications";
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress";
import { mergeAngularFullFormParityFields } from "@/features/company/utils/companyValidators";
import { format } from "date-fns";
import {
    formatCommunicationLogCreatedAt,
    formatCommunicationLogDateTime,
} from "@/lib/utils/communicationLogDates";
import {
    AdvancedDateRangePicker,
    ANGULAR_COMMUNICATION_LOG_PRESETS,
} from "@/components/ui/AdvancedDateRangePicker";
import {
    CommunicationLogViewModal,
} from "@/features/company/components/CommunicationLogViewModal";
import {
    ACTIVITY_INITIAL_PAGINATION,
    COMMUNICATION_CATEGORY,
    COMMUNICATION_DIRECTION,
    COMMUNICATION_TYPES,
} from "../../constants";
import { TableSearchFilterEmptyPanel } from "@/components/ui/TableSearchFilterEmptyState";
import { SectionCard } from "@/components/ui/SectionCard";
import companyFormStyles from "../../styles/companyAdd.module.css";
import tabStyles from "../../styles/companyInfoTab.module.css";
import type {
    ActivityFilters,
    ActivityMemberRow,
    ActivityPropertyUserRow,
    ActivitySortBy,
    ActivityTabProps,
    CommunicationLogOrderBy,
    CommunicationLogRow,
} from "../../types";

const FILTER_FIELD = cn(COMM_FIELD_BASE, "h-10 w-full");

export function ActivityTab({ companyId, company, onUpdate }: ActivityTabProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [logsList, setLogsList] = useState<CommunicationLogRow[]>([]);
    /** Total rows for current filters — Angular `response.total` on communication log list. */
    const [totalRecords, setTotalRecords] = useState(0);

    const [propertyUserList, setPropertyUserList] = useState<ActivityPropertyUserRow[]>([]);
    const [memberList, setMemberList] = useState<ActivityMemberRow[]>([]);

    const [filters, setFilters] = useState<ActivityFilters>({
        type: "",
        linked_contact: "",
        member_id: "",
    });

    /** Created-date range — Angular stores `daterange: { fromDt, toDt }` and sends `fromDt`/`toDt` as DD-MM-YYYY. */
    const [createdRangeStart, setCreatedRangeStart] = useState<Date | null>(null);
    const [createdRangeEnd, setCreatedRangeEnd] = useState<Date | null>(null);

    const [pagination, setPagination] = useState(ACTIVITY_INITIAL_PAGINATION);

    const [viewLogOpen, setViewLogOpen] = useState(false);
    const [viewLog, setViewLog] = useState<CommunicationLogRow | null>(null);

    useEffect(() => {
        setFilters({ type: "", linked_contact: "", member_id: "" });
        setCreatedRangeStart(null);
        setCreatedRangeEnd(null);
        setPagination(ACTIVITY_INITIAL_PAGINATION);
        setTotalRecords(0);
        loadDropdownData();
        loadLogs(
            { type: "", linked_contact: "", member_id: "" },
            ACTIVITY_INITIAL_PAGINATION,
            null,
            null
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const loadDropdownData = async () => {
        try {
            const [usersRes, membersRes] = await Promise.all([
                apiClient.get(`/api/property_staff`, { params: { company_id: companyId } }),
                apiClient.get(`/api/member_communication_log/${companyId}`, { params: { pmc_type: "COMPANY" } })
            ]);
            setPropertyUserList(usersRes.data || []);
            setMemberList(membersRes.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadLogs = async (
        currentFilters: ActivityFilters,
        currentPagination: typeof ACTIVITY_INITIAL_PAGINATION,
        rangeStart: Date | null,
        rangeEnd: Date | null
    ) => {
        setIsLoading(true);
        try {
            const params: Record<string, string | number> = {
                page: currentPagination.page,
                perPage: currentPagination.perPage,
                sortBy: currentPagination.sortBy,
                orderBy: currentPagination.orderBy,
                pmc_type: "COMPANY",
            };
            if (currentFilters.type) params.type = currentFilters.type;
            if (currentFilters.linked_contact) params.linked_contact = currentFilters.linked_contact;
            if (currentFilters.member_id) params.member_id = currentFilters.member_id;
            if (rangeStart && rangeEnd) {
                params.fromDt = format(rangeStart, "dd-MM-yyyy");
                params.toDt = format(rangeEnd, "dd-MM-yyyy");
            }

            const response = await apiClient.get(`/api/communication_log/${companyId}`, { params });
            const rows = response.data?.data || [];
            setLogsList(rows);
            const rawTotal = response.data?.total ?? response.data?.meta?.total;
            setTotalRecords(typeof rawTotal === "number" ? rawTotal : rows.length);

        } catch (error) {
            console.error(error);
            showError("Failed to load communication history");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        const nextPage = { ...pagination, page: 1 };
        setPagination(nextPage);
        loadLogs(filters, nextPage, createdRangeStart, createdRangeEnd);
    };

    const handleReset = () => {
        const resetFilters: ActivityFilters = { type: "", linked_contact: "", member_id: "" };
        const resetPagination = { ...ACTIVITY_INITIAL_PAGINATION };
        setFilters(resetFilters);
        setCreatedRangeStart(null);
        setCreatedRangeEnd(null);
        setPagination(resetPagination);
        loadLogs(resetFilters, resetPagination, null, null);
    };

    const handleCreatedDateChange = (start: Date | null, end: Date | null) => {
        setCreatedRangeStart(start);
        setCreatedRangeEnd(end);
    };

    /** Same as Angular `sort(orderBy)`: toggle asc/desc and set `orderBy`, reset to page 1. */
    const handleSort = (orderBy: CommunicationLogOrderBy) => {
        if (isLoading) return;
        const nextSort: ActivitySortBy = pagination.sortBy === "asc" ? "desc" : "asc";
        const next = {
            ...pagination,
            page: 1,
            orderBy,
            sortBy: nextSort,
        };
        setPagination(next);
        loadLogs(filters, next, createdRangeStart, createdRangeEnd);
    };

    /**
     * Angular `communication-log-list.component.ts` `export()`:
     * `GET /api/export/communication_log/:companyId?...filters&access_token&pmc_type&view=xlsx`
     */
    const communicationLogExportUrl = useMemo(() => {
        const token = getAuthTokenFromStorage() || "";
        if (!companyId || !token) return "";
        const params = new URLSearchParams();
        if (createdRangeStart && createdRangeEnd) {
            params.set("fromDt", format(createdRangeStart, "dd-MM-yyyy"));
            params.set("toDt", format(createdRangeEnd, "dd-MM-yyyy"));
        }
        params.set("page", String(pagination.page));
        params.set("perPage", String(pagination.perPage));
        params.set("sortBy", pagination.sortBy);
        params.set("orderBy", pagination.orderBy);
        if (filters.type) params.set("type", filters.type);
        if (filters.linked_contact) params.set("linked_contact", String(filters.linked_contact));
        if (filters.member_id) params.set("member_id", String(filters.member_id));
        params.set("pmc_type", "COMPANY");
        params.set("access_token", token);
        params.set("view", "xlsx");
        const base = getBrowserApiBaseUrl();
        return `${base}/api/export/communication_log/${companyId}?${params.toString()}`;
    }, [companyId, filters, pagination, createdRangeStart, createdRangeEnd]);

    const SortableTh = ({
        column,
        children,
        className = "",
    }: {
        column: CommunicationLogOrderBy;
        children: React.ReactNode;
        className?: string;
    }) => {
        const active = pagination.orderBy === column;
        return (
            <th
                scope="col"
                className={`px-3 py-2.5 text-left text-xs font-medium text-slate-500 ${className} ${
                    isLoading ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-slate-100/90"
                }`}
                onClick={() => handleSort(column)}
            >
                <span className="inline-flex flex-wrap items-center gap-1">
                    {children}
                    {active ? (
                        pagination.sortBy === "desc" ? (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[rgb(21_151_255)]" aria-hidden />
                        ) : (
                            <ChevronUp className="h-3.5 w-3.5 shrink-0 text-[rgb(21_151_255)]" aria-hidden />
                        )
                    ) : (
                        <span className="inline-block w-3.5 shrink-0" aria-hidden />
                    )}
                </span>
            </th>
        );
    };

    /** Header Save → `POST /api/company/:id` with Angular parity fields only (no activity-specific form data). */
    const onActivityFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            await onUpdate(
                mergeAngularFullFormParityFields(company as Record<string, unknown> | undefined),
            );
            showSuccess("Company Updated  Successfully!");
        } catch (err: unknown) {
            if (isCompanyDuplicateAddressCancelledError(err)) return;
            showError(formatApiError(err));
        }
    };

    return (
        <form
            data-testid="company-activity-tab-form"
            data-tab-id="activity"
            className={tabStyles.tabContent}
            onSubmit={onActivityFormSubmit}
            noValidate
        >
            <SectionCard
                icon={History}
                title="Communication History"
                testId="company-activity-communication-history-section"
                headerActions={
                    logsList.length > 0 && communicationLogExportUrl ? (
                        <a
                            data-testid="company-activity-export-link"
                            href={communicationLogExportUrl}
                            className={cn(
                                companyFormStyles.btnPrimary,
                                "inline-flex min-w-[100px] justify-center no-underline",
                            )}
                        >
                            Export
                        </a>
                    ) : null
                }
            >
                <div
                    data-testid="company-activity-filters-bar"
                    className="flex min-w-0 flex-wrap items-end gap-4"
                >
                    <div
                        className="w-full min-w-0 max-w-md flex-1"
                        data-testid="company-activity-created-date-range"
                    >
                        <label className={COMM_LABEL}>Created Date</label>
                        <AdvancedDateRangePicker
                            startDate={createdRangeStart}
                            endDate={createdRangeEnd}
                            onChange={handleCreatedDateChange}
                            placeholder="Select Date Range"
                            customPresets={ANGULAR_COMMUNICATION_LOG_PRESETS}
                            showApplyButton
                            presetDeferUntilApply
                            applyButtonPlacement="top"
                            displayRangeFormat="angularMmddyyyy"
                            monthsShown={2}
                            dropdownPortalToBody
                            hideTriggerIcon
                            className="w-full"
                            triggerClassName="min-h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm ring-0 focus:ring-0"
                        />
                    </div>

                    <div className="w-full min-w-0 sm:w-48">
                        <label className={COMM_LABEL}>Type</label>
                        <select
                            data-testid="company-activity-filter-type-select"
                            value={filters.type}
                            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                            className={FILTER_FIELD}
                        >
                            <option value="">Select Type</option>
                            {Object.entries(COMMUNICATION_TYPES).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                            ))}
                        </select>
                    </div>

                    <div className="w-full min-w-0 sm:w-48">
                        <label className={COMM_LABEL}>Linked Contact</label>
                        <select
                            data-testid="company-activity-filter-linked-contact-select"
                            value={filters.linked_contact}
                            onChange={(e) => setFilters(prev => ({ ...prev, linked_contact: e.target.value }))}
                            className={FILTER_FIELD}
                        >
                            <option value="">Select Contact</option>
                            {propertyUserList.map(user => (
                                <option key={user.user_id} value={user.user_id}>{user.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex w-full min-w-0 flex-wrap items-end gap-2 sm:w-auto">
                        <div className="w-full min-w-0 sm:w-48">
                            <label className={COMM_LABEL}>Team Member</label>
                            <select
                                data-testid="company-activity-filter-team-member-select"
                                value={filters.member_id}
                                onChange={(e) => setFilters(prev => ({ ...prev, member_id: e.target.value }))}
                                className={FILTER_FIELD}
                            >
                                <option value="">Select Contact</option>
                                {memberList.map(member => (
                                    <option key={member.fk_user_id} value={member.fk_user_id}>{member.name}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            data-testid="company-activity-search-button"
                            onClick={handleSearch}
                            disabled={isLoading}
                            className={cn(companyFormStyles.btnPrimary, "min-w-[100px] justify-center")}
                        >
                            Search
                        </button>
                        <button
                            type="button"
                            data-testid="company-activity-reset-button"
                            onClick={handleReset}
                            disabled={isLoading}
                            className="min-w-[100px] rounded-lg border border-slate-200 bg-white px-6 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                        >
                            Reset
                        </button>
                    </div>
                </div>

            <CommunicationLogViewModal
                isOpen={viewLogOpen && viewLog != null}
                onClose={() => {
                    setViewLogOpen(false);
                    setViewLog(null);
                }}
                log={viewLog}
                onCommentUpdated={() => {
                    loadLogs(filters, pagination, createdRangeStart, createdRangeEnd);
                }}
            />

            {/* Table Area */}
            {isLoading ? (
                <div
                    data-testid="company-activity-table-loading"
                    className="flex items-center justify-center rounded-lg border border-slate-200 bg-white py-20"
                >
                    <Loader2 className="h-6 w-6 animate-spin text-[rgb(21_151_255)]" />
                </div>
            ) : logsList.length === 0 ? (
                <TableSearchFilterEmptyPanel
                    data-testid="company-activity-table-empty"
                />
            ) : (
                <div
                    data-testid="company-activity-logs-table-wrapper"
                    className="w-full min-w-0 overflow-x-auto rounded-lg border border-slate-200"
                >
                    <table
                        data-testid="company-activity-logs-table"
                        className="w-full min-w-[960px] border-collapse text-[13px]"
                    >
                        <thead
                            data-testid="company-activity-logs-thead"
                            className="bg-slate-50/80"
                        >
                            <tr className="border-b border-slate-200">
                                <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Contacts</th>
                                <SortableTh column="log_datetime">Log Date / Time</SortableTh>
                                <SortableTh column="created_at">Created Date / Time</SortableTh>
                                <SortableTh column="type">Type</SortableTh>
                                <SortableTh column="category">Category</SortableTh>
                                <SortableTh column="direction">Direction</SortableTh>
                                <SortableTh column="first_name">User</SortableTh>
                                <SortableTh column="comment">Description</SortableTh>
                                <th className="whitespace-nowrap px-3 py-2.5 text-left text-xs font-medium text-slate-500">
                                    Content
                                </th>
                            </tr>
                        </thead>
                        <tbody data-testid="company-activity-logs-tbody">
                            {logsList.map((log, idx) => (
                                <tr
                                    key={idx}
                                    className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50"
                                >
                                    <td className="px-3 py-3 align-middle text-slate-700">
                                        {log.linked_contact_users || "—"}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-slate-500">
                                        {formatCommunicationLogDateTime(log.log_datetime) || "—"}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-slate-500">
                                        {formatCommunicationLogCreatedAt(log.created_at) || "—"}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-slate-500">
                                        {(log.type && COMMUNICATION_TYPES[log.type]) || log.type || "—"}
                                        {log.type === "OTHER" && log.type_other ? ` - ${log.type_other}` : ""}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-slate-500">
                                        {(log.category && COMMUNICATION_CATEGORY[log.category]) || log.category || "—"}
                                        {log.category === "OTHER" && log.category_other ? ` - ${log.category_other}` : ""}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-slate-500">
                                        {(log.direction && COMMUNICATION_DIRECTION[log.direction]) || log.direction || "—"}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-slate-500">
                                        {[log.first_name, log.last_name].filter(Boolean).join(" ") || "—"}
                                    </td>
                                    <td className="max-w-[280px] px-3 py-3 align-middle text-slate-700">
                                        <div
                                            className="company-activity-log-comment max-h-40 min-w-0 overflow-y-auto overflow-x-hidden text-left leading-relaxed [overflow-wrap:anywhere] [word-break:break-word] [&_a]:break-words [&_a]:text-[rgb(21_151_255)] [&_a]:underline-offset-2 hover:[&_a]:underline [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-200 [&_blockquote]:pl-3 [&_div]:max-w-full [&_img]:h-auto [&_img]:max-w-full [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_p]:[overflow-wrap:anywhere] [&_p]:first:mt-0 [&_p]:last:mb-0 [&_table]:max-w-full [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
                                            dangerouslySetInnerHTML={{ __html: log.comment || "" }}
                                        />
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3 align-middle">
                                        <button
                                            type="button"
                                            data-testid={`company-activity-log-row-${log.log_id != null ? String(log.log_id) : `idx-${idx}`}-view-button`}
                                            onClick={() => {
                                                setViewLog(log as CommunicationLogRow);
                                                setViewLogOpen(true);
                                            }}
                                            className="cursor-pointer border-0 bg-transparent p-0 font-medium text-[rgb(21_151_255)] hover:underline"
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination — Angular: record count left, Per Page centered */}
            {logsList.length > 0 && (
                <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
                    <span className="justify-self-start text-sm text-slate-600">
                        ({totalRecords} {totalRecords === 1 ? "Record" : "Records"})
                    </span>
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-slate-600">Per Page :</span>
                        <select
                            data-testid="company-activity-per-page-select"
                            value={pagination.perPage}
                            onChange={(e) => {
                                const perPage = Number(e.target.value);
                                const next = { ...pagination, perPage, page: 1 };
                                setPagination(next);
                                loadLogs(filters, next, createdRangeStart, createdRangeEnd);
                            }}
                            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none"
                        >
                            <option value="15">15</option>
                            <option value="30">30</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <span className="hidden sm:block" aria-hidden="true" />
                </div>
            )}

            </SectionCard>

            {/* Header Save → Confirm → `requestSubmit()` (needs a submit control per HTML). */}
            <button
                type="submit"
                data-testid="company-activity-hidden-submit"
                tabIndex={-1}
                aria-hidden
                className="pointer-events-none absolute h-0 w-0 opacity-0"
            />
        </form>
    );
}
